import * as React from 'react';
import { Plus, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMaterials } from '@/hooks/catalogue/use-materials';
import { useWindowTypes } from '@/hooks/catalogue/use-window-types';
import { useCatalogueSeries } from '@/hooks/catalogue/use-catalogue-series';
import { useSashes } from '@/hooks/catalogue/use-sashes';
import { useCatalogueTypes } from '@/hooks/catalogue/use-catalogue-types';
import { useCombinations } from '@/hooks/catalogue/use-combinations';
import { useCreateLineItem } from '@/hooks/line-items/use-create-line-item';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { DynamicLineItemForm } from '@/components/DynamicLineItemForm';

interface LineItemCalculatorFormProps {
  projectId: number;
}

type CascadeState = {
  materialId?: number;
  materialCode?: string;
  windowTypeId?: number;
  seriesId?: number;
  sashId?: number;
  typeId?: number;
  frameStyle?: string;
};

const INITIAL_CASCADE: CascadeState = {};

const FRAME_STYLE_OPTIONS = [
  { value: 'WITH_MULLION', label: 'With Mullion' },
  { value: 'SEPARATE_FRAME', label: 'Separate Frame' },
];

export function LineItemCalculatorForm({ projectId }: LineItemCalculatorFormProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = React.useState(true);
  const [cascade, setCascade] = React.useState<CascadeState>(INITIAL_CASCADE);
  const [resetKey, setResetKey] = React.useState(0);

  const { data: materials = [], isLoading: materialsLoading } = useMaterials();
  const { data: windowTypes = [], isLoading: windowTypesLoading } = useWindowTypes(
    cascade.materialId,
  );
  const { data: seriesList = [], isLoading: seriesLoading } = useCatalogueSeries(
    cascade.materialCode as any,
    cascade.windowTypeId,
  );
  const { data: sashes = [], isLoading: sashesLoading } = useSashes(
    cascade.materialId,
    cascade.windowTypeId,
    cascade.seriesId,
  );
  const { data: types = [], isLoading: typesLoading } = useCatalogueTypes(
    cascade.seriesId,
    cascade.windowTypeId,
  );

  const { data: combinations = [], isLoading: combinationsLoading } = useCombinations(
    cascade.materialId,
    cascade.windowTypeId,
    cascade.seriesId,
    cascade.typeId,
    cascade.sashId,
    cascade.frameStyle,
  );

  const createLineItemMutation = useCreateLineItem();

  const handleCascadeChange = (field: keyof CascadeState, value: any) => {
    setCascade((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'materialId') {
        const mat = materials.find((m) => m.id === value);
        next.materialCode = mat?.code;
        next.windowTypeId = undefined;
        next.seriesId = undefined;
        next.sashId = undefined;
        next.typeId = undefined;
        next.frameStyle = undefined;
      }
      if (field === 'windowTypeId') {
        next.seriesId = undefined;
        next.sashId = undefined;
        next.typeId = undefined;
        next.frameStyle = undefined;
      }
      if (field === 'seriesId') {
        next.sashId = undefined;
        next.typeId = undefined;
        next.frameStyle = undefined;
      }
      if (field === 'typeId') {
        next.frameStyle = undefined;
      }
      return next;
    });
  };

  const selectedCombination = combinations.length === 1 ? combinations[0] : null;

  const handleSubmitLineItem = (dynamicValues: Record<string, any>) => {
    createLineItemMutation.mutate(
      { projectId, input: { ...dynamicValues, material: cascade.materialCode } as any },
      {
        onSuccess: () => {
          toast({ title: 'Line item added' });
          setCascade(INITIAL_CASCADE);
          setResetKey((k) => k + 1);
          setIsOpen(false);
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message ?? '';
          toast({
            variant: 'destructive',
            title: 'Failed to add line item',
            description: msg || 'An unexpected error occurred.',
          });
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md border border-dashed border-border px-4 py-2.5 text-sm text-fg-secondary transition-colors hover:border-primary/50 hover:text-fg-primary"
      >
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        <span className="font-medium">{isOpen ? 'Close calculator' : 'Add line item'}</span>
      </button>

      {isOpen && (
        <div className="flex flex-col gap-4">
          <Card key={resetKey}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Material */}
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Material <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={materials.map((m) => ({ value: String(m.id), label: m.label }))}
                    value={cascade.materialId ? String(cascade.materialId) : undefined}
                    onValueChange={(v) =>
                      handleCascadeChange('materialId', v ? Number(v) : undefined)
                    }
                    placeholder={materialsLoading ? 'Loading...' : 'Select material'}
                    searchPlaceholder="Search material..."
                    disabled={materialsLoading}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Window Type */}
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Window Type <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={windowTypes.map((w) => ({ value: String(w.id), label: w.label }))}
                    value={cascade.windowTypeId ? String(cascade.windowTypeId) : undefined}
                    onValueChange={(v) =>
                      handleCascadeChange('windowTypeId', v ? Number(v) : undefined)
                    }
                    placeholder={
                      !cascade.materialId
                        ? 'Select Material first'
                        : windowTypesLoading
                          ? 'Loading...'
                          : 'Select window type'
                    }
                    searchPlaceholder="Search window type..."
                    disabled={!cascade.materialId || windowTypesLoading}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Series */}
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Series <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={seriesList.map((s) => ({ value: String(s.id), label: s.label }))}
                    value={cascade.seriesId ? String(cascade.seriesId) : undefined}
                    onValueChange={(v) =>
                      handleCascadeChange('seriesId', v ? Number(v) : undefined)
                    }
                    placeholder={
                      !cascade.windowTypeId
                        ? 'Select Window Type first'
                        : seriesLoading
                          ? 'Loading...'
                          : 'Select series'
                    }
                    searchPlaceholder="Search series..."
                    disabled={!cascade.windowTypeId || seriesLoading}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Sash (optional) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Sash</Label>
                  <Combobox
                    options={sashes.map((s) => ({ value: String(s.id), label: s.label }))}
                    value={cascade.sashId ? String(cascade.sashId) : undefined}
                    onValueChange={(v) => handleCascadeChange('sashId', v ? Number(v) : undefined)}
                    placeholder={
                      !cascade.seriesId
                        ? 'Select Series first'
                        : sashesLoading
                          ? 'Loading...'
                          : sashes.length === 0
                            ? 'Not applicable'
                            : 'Select sash'
                    }
                    searchPlaceholder="Search sash..."
                    disabled={!cascade.seriesId || sashesLoading || sashes.length === 0}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Type */}
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Type <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={types.map((t) => ({ value: String(t.id), label: t.label }))}
                    value={cascade.typeId ? String(cascade.typeId) : undefined}
                    onValueChange={(v) => handleCascadeChange('typeId', v ? Number(v) : undefined)}
                    placeholder={
                      !cascade.seriesId
                        ? 'Select Series first'
                        : typesLoading
                          ? 'Loading...'
                          : 'Select type'
                    }
                    searchPlaceholder="Search type..."
                    disabled={!cascade.seriesId || typesLoading}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Frame Style (optional) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Frame Style</Label>
                  <Combobox
                    options={FRAME_STYLE_OPTIONS}
                    value={cascade.frameStyle}
                    onValueChange={(v) => handleCascadeChange('frameStyle', v)}
                    placeholder="Select (if applicable)"
                    searchPlaceholder="Search frame style..."
                    disabled={!cascade.typeId}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {cascade.typeId && combinationsLoading && (
                <p className="mt-3 text-xs text-fg-muted">Resolving combination...</p>
              )}
              {cascade.typeId && !combinationsLoading && combinations.length === 0 && (
                <p className="mt-3 text-xs text-destructive">
                  This combination is not supported or not fully selected.
                </p>
              )}
              {cascade.typeId && !combinationsLoading && combinations.length > 1 && (
                <p className="mt-3 text-xs text-fg-muted">
                  Multiple combinations matched. Please refine selection (e.g. Sash or Frame Style).
                </p>
              )}
            </CardContent>
          </Card>

          {selectedCombination && cascade.materialCode && (
            <DynamicLineItemForm
              combinationId={selectedCombination.id}
              materialCode={cascade.materialCode}
              onSubmit={handleSubmitLineItem}
              onReset={() => {
                setCascade(INITIAL_CASCADE);
                setResetKey((k) => k + 1);
              }}
              isSubmitting={createLineItemMutation.isPending}
              submitLabel="Add to Project"
            />
          )}
        </div>
      )}
    </div>
  );
}
